import axios from 'axios'

import { RUN_TERMINAL_STATUSES } from 'Pods/ThreeDStudio/constants'
import { threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { IMAGE_ACTION } from 'Pods/ThreeDStudio/Store/constants'

import { imageActionOf } from './imageModels'

// Brush edits and single-image instruction edits (prompt) are produced by the Edit screen
// and belong to its version history, not to the Create tab conversation. Both restore
// from the same session endpoint, so the run's model is what tells them apart - matched
// by action so an edit is recognised whichever provider ran it, including runs stored
// before the provider changed. An unknown model id is never an edit, so an unexpected
// payload keeps the conversation rather than silently dropping it.
//
// A brush edit is a multi-image-edit whose second image is the painted overlay, flagged
// by overlay_mask on the provider run (served snake_case; the camelCase form is accepted
// like the other run attributes). The Create tab's reference blends share the model but
// not the flag, so they stay in the conversation. Inpaint is the earlier form of the same
// edit, kept so older sessions still restore.
const hasOverlayMask = (run) => {
  const attributes = run?.providerRunAttributes

  return attributes?.overlay_mask === true || attributes?.overlayMask === true
}

export const isMaskedEditRun = (run) => {
  const action = imageActionOf(run?.providerModelId)

  return action === IMAGE_ACTION.INPAINT || (action === IMAGE_ACTION.MULTI_IMAGE_EDIT && hasOverlayMask(run))
}

export const isImageEditRun = run => imageActionOf(run?.providerModelId) === IMAGE_ACTION.IMAGE_EDIT

export const isEditScreenRun = run => isMaskedEditRun(run) || isImageEditRun(run)

// How a version in the Edit screen's history was produced: painting a mask (inpaint run)
// or a bare instruction (image-edit run). Drives the history row subtitle and the
// prompt-viewer icon.
export const VERSION_KIND = {
  BRUSH: 'brush',
  PROMPT: 'prompt',
}

export const editKindOfRun = (run) => {
  if (isMaskedEditRun(run)) {
    return VERSION_KIND.BRUSH
  }

  return isImageEditRun(run) ? VERSION_KIND.PROMPT : undefined
}

// The session the assistant is working in: the one its own runs landed in, else the
// opened model's. When there is none the backend creates a draft session for the first
// run - which is what a fresh composition ("Generate New 3D Model" clears both signals)
// must do, rather than inheriting the session of the model it replaced.
//
// The assistant's own id comes first and lives in the ui slice so that closing the
// panel (which remounts the tabs) can still restore the conversation - the 3D run
// lifecycle resets generation.sessionId, which used to lose it.
//
// generation.sessionId is deliberately NOT a fallback: the openedModelToStatePatch
// parsers report the opened *run* id in that field, so posting it as
// generator_session_id made the backend 404 on the session lookup.
export const selectSessionId = state => (
  state.ui?.imageAssistantSessionId
  ?? state.ui?.openedModel?.generatorSessionId
)

export const getCurrentSessionId = () => selectSessionId(threeDStudioStore.getState())

// The Image Generator page's own conversation. No opened-model fallback: the page is not
// scoped to a model, so it never joins one's session.
export const selectImageGeneratorSessionId = state => state.ui?.imageGeneratorSessionId

export const getImageGeneratorSessionId = () => selectImageGeneratorSessionId(threeDStudioStore.getState())

// Subscribes to a run's websocket channel. Calls onImage(asset, assets) with the first
// output image and every output image (a run with several variations returns up to four)
// on terminal success, or onFail() on a missing asset / rejected subscription.
// Returns the subscription (or null when Action Cable is unavailable) so the caller can
// track it for cleanup.
export function subscribeToGeneratorRun(runId, { onImage, onFail }) {
  if (!runId || !window.App?.cable?.subscriptions) {
    return null
  }

  let subscription = null
  let isSettled = false

  // Settles the turn exactly once, from whichever source reports the outcome first:
  // the terminal broadcast, the reconciliation below, or a rejected subscription.
  const settle = (outputAssets) => {
    if (isSettled) {
      return
    }
    isSettled = true
    // Unsubscribing from inside received() mutates the array Action Cable is iterating
    // in Subscriptions#notify, so a sibling subscription on the same channel is read
    // past the end ("Cannot read properties of undefined (reading 'received')").
    // Deferring to a microtask lets that loop finish first.
    Promise.resolve().then(() => subscription?.unsubscribe())
    const assets = (outputAssets || []).filter(asset => asset?.fileUrl)
    if (assets.length > 0) {
      onImage(assets[0], assets)
    } else {
      onFail()
    }
  }

  // A run that settled before this subscription's stream_from registered - it finished
  // while the panel was closed, or failed within a couple of seconds - fired its
  // terminal broadcast into the void, since ActionCable does not replay. Without this
  // the turn spins forever. Mirrors useGeneratorRunChannel's reconcileOnConnect.
  const reconcile = () => {
    axios.get(`/api/internal/generator/runs/${runId}`)
      .then(({ data }) => {
        const attributes = data?.data?.attributes
        if (attributes && RUN_TERMINAL_STATUSES.has(attributes.status)) {
          settle(attributes.outputAssets)
        }
      })
      // A failed reconciliation is a no-op: the broadcast is still the primary path.
      .catch(() => {})
  }

  subscription = window.App.cable.subscriptions.create(
    { channel: 'GeneratorRunsChannel', run_id: runId },
    {
      connected() {
        reconcile()
      },
      received(message) {
        if (message?.type !== 'generator/runs/update') {
          return
        }
        settle(message?.data?.attributes?.outputAssets)
      },
      rejected() {
        settle(undefined)
      },
    },
  )

  return subscription
}
